import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCourtGroups } from "@/hooks/useCourtGroups";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Loader2, Pencil, Plus, Trash2 } from "lucide-react";

interface Props {
  hallId: string;
  organizationId: string;
}

export default function SettingsCourtGroups({ hallId, organizationId }: Props) {
  const { t } = useTranslation("admin");
  const { groups, isLoading, createGroup, updateGroup, deleteGroup, assignCourtToGroup } = useCourtGroups(hallId);
  const [showCreate, setShowCreate] = useState(false);
  const [editGroup, setEditGroup] = useState<(typeof groups)[0] | null>(null);
  const [formName, setFormName] = useState("");
  const [formDisplayName, setFormDisplayName] = useState("");
  const [formDescription, setFormDescription] = useState("");

  // Fetch courts for this hall to show assignment
  const { data: courts = [] } = useQuery({
    queryKey: ["admin-courts-for-groups", hallId],
    queryFn: async () => {
      const { data } = await supabase
        .from("courts")
        .select("id, name, court_group_id, status, sort_order")
        .eq("hall_id", hallId)
        .eq("status", "active")
        .order("sort_order");
      return (data ?? []) as Array<{ id: string; name: string; court_group_id: string | null; status: string; sort_order: number }>;
    },
    enabled: !!hallId,
  });

  const unassignedCourts = courts.filter((c) => !c.court_group_id);

  const openCreate = () => {
    setFormName(""); setFormDisplayName(""); setFormDescription("");
    setShowCreate(true);
  };

  const openEdit = (g: (typeof groups)[0]) => {
    setFormName(g.name);
    setFormDisplayName(g.display_name);
    setFormDescription(g.description ?? "");
    setEditGroup(g);
  };

  const handleCreate = () => {
    createGroup.mutate(
      { name: formName, display_name: formDisplayName, description: formDescription, organization_id: organizationId },
      { onSuccess: () => setShowCreate(false) },
    );
  };

  const handleUpdate = () => {
    if (!editGroup) return;
    updateGroup.mutate(
      { id: editGroup.id, name: formName, display_name: formDisplayName, description: formDescription },
      { onSuccess: () => setEditGroup(null) },
    );
  };

  return (
    <div className="space-y-3 pt-4 border-t">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">{t("settings.courtGroups.title")}</h4>
        <Button size="sm" variant="outline" onClick={openCreate}>
          <Plus className="h-3.5 w-3.5 mr-1" /> {t("settings.courtGroups.btnAdd")}
        </Button>
      </div>

      {/* Warning: unassigned courts */}
      {unassignedCourts.length > 0 && (
        <div className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{t("settings.courtGroups.unassignedWarning", { count: unassignedCourts.length, courts: unassignedCourts.map((c) => c.name).join(", ") })}</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin" /></div>
      ) : groups.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4">{t("settings.courtGroups.emptyState")}</p>
      ) : (
        <div className="space-y-2">
          {groups.map((g) => {
            const groupCourts = courts.filter((c) => c.court_group_id === g.id);
            return (
              <Card key={g.id}>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{g.display_name}</p>
                      {g.description && <p className="text-xs text-muted-foreground">{g.description}</p>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge variant="secondary" className="text-xs">{t("settings.courtGroups.courtCount", { count: g.court_count ?? groupCourts.length })}</Badge>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(g)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteGroup.mutate(g.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  {/* Court assignment */}
                  <div className="flex flex-wrap gap-1">
                    {groupCourts.map((c) => (
                      <Badge key={c.id} variant="outline" className="text-xs">{c.name}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Court assignment dropdowns */}
          <div className="space-y-1.5 pt-2">
            <Label className="text-xs font-medium">{t("settings.courtGroups.assignCourts")}</Label>
            {courts.map((c) => (
              <div key={c.id} className="flex items-center gap-2">
                <span className="text-xs w-20 truncate">{c.name}</span>
                <Select
                  value={c.court_group_id ?? "none"}
                  onValueChange={(v) => assignCourtToGroup.mutate({ courtId: c.id, groupId: v === "none" ? null : v })}
                >
                  <SelectTrigger className="h-7 text-xs flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("settings.courtGroups.noCategory")}</SelectItem>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>{g.display_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={showCreate || !!editGroup} onOpenChange={(v) => { if (!v) { setShowCreate(false); setEditGroup(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editGroup ? t("settings.courtGroups.dialogEditTitle") : t("settings.courtGroups.dialogCreateTitle")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">{t("settings.courtGroups.internalName")}</Label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder={t("settings.courtGroups.internalNamePlaceholder")} className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("settings.courtGroups.displayName")}</Label>
              <Input value={formDisplayName} onChange={(e) => setFormDisplayName(e.target.value)} placeholder={t("settings.courtGroups.displayNamePlaceholder")} className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("settings.courtGroups.descriptionLabel")}</Label>
              <Input value={formDescription} onChange={(e) => setFormDescription(e.target.value)} placeholder={t("settings.courtGroups.descriptionPlaceholder")} className="h-8 text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditGroup(null); }}>{t("settings.courtGroups.btnCancel")}</Button>
            <Button
              onClick={editGroup ? handleUpdate : handleCreate}
              disabled={!formName.trim() || !formDisplayName.trim() || createGroup.isPending || updateGroup.isPending}
            >
              {(createGroup.isPending || updateGroup.isPending) && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editGroup ? t("settings.courtGroups.btnSave") : t("settings.courtGroups.btnCreate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
